import { ref, onUnmounted } from 'vue'
import { useIntervalFn } from '@vueuse/core'

export interface AudioRecorderOptions {
  onDataAvailable?: (blob: Blob) => void
  onStop?: (blob: Blob, duration: number) => void
  onError?: (error: Error) => void
}

export const useAudioRecorder = (options: AudioRecorderOptions = {}) => {
  const isRecording = ref(false)
  const duration = ref(0)
  const audioBlob = ref<Blob | null>(null)

  const mediaRecorderRef = ref<MediaRecorder | null>(null)
  const chunksRef = ref<Blob[]>([])
  const audioContextRef = ref<AudioContext | null>(null)
  const analyserRef = ref<AnalyserNode | null>(null)
  const dataArrayRef = ref<Uint8Array | null>(null)
  const streamRef = ref<MediaStream | null>(null)

  const { pause: pauseTimer, resume: resumeTimer } = useIntervalFn(() => {
    duration.value++
  }, 1000, { immediate: false })

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      streamRef.value = stream

      // Priority: ogg/opus (Meta ✓, Chrome/Firefox/Edge) → webm/opus (fallback, not Meta-compatible)
      // audio/mp4 is excluded: MediaRecorder produces non-standard blobs that Meta rejects
      // (Meta receives audio/mp4 but detects application/octet-stream internally)
      const mimeTypeCandidates = [
        'audio/ogg; codecs=opus',
        'audio/webm; codecs=opus',
      ]
      const audioBitsPerSecond = 32000

      const selectedMimeType = mimeTypeCandidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? ''

      const recorderOptions: MediaRecorderOptions = selectedMimeType
        ? { mimeType: selectedMimeType, audioBitsPerSecond }
        : { audioBitsPerSecond }

      const mediaRecorder = new MediaRecorder(stream, recorderOptions)
      mediaRecorderRef.value = mediaRecorder
      chunksRef.value = []

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.value.push(e.data)
          options.onDataAvailable?.(e.data)
        }
      }

      mediaRecorder.onstop = () => {
        const actualMimeType = chunksRef.value[0]?.type || recorderOptions.mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.value, { type: actualMimeType })
        audioBlob.value = blob

        stream.getTracks().forEach((track) => track.stop())
        options.onStop?.(blob, duration.value)
      }

      audioContextRef.value = new AudioContext()
      const source = audioContextRef.value.createMediaStreamSource(stream)
      analyserRef.value = audioContextRef.value.createAnalyser()

      analyserRef.value.fftSize = 512
      analyserRef.value.smoothingTimeConstant = 0.85
      analyserRef.value.minDecibels = -90
      analyserRef.value.maxDecibels = -10

      source.connect(analyserRef.value)

      const bufferLength = analyserRef.value.frequencyBinCount
      dataArrayRef.value = new Uint8Array(bufferLength)

      mediaRecorder.start()
      isRecording.value = true
      resumeTimer()
    } catch (error) {
      options.onError?.(error as Error)
      throw error
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.value && isRecording.value) {
      mediaRecorderRef.value.stop()
      isRecording.value = false
      pauseTimer()

      if (audioContextRef.value) {
        audioContextRef.value.close()
        audioContextRef.value = null
      }
    }
  }

  const reset = () => {
    stopRecording()
    duration.value = 0
    audioBlob.value = null
    chunksRef.value = []
  }

  onUnmounted(() => {
    stopRecording()
    if (streamRef.value) {
      streamRef.value.getTracks().forEach((track) => track.stop())
    }
  })

  return {
    isRecording,
    duration,
    audioBlob,
    analyser: analyserRef,
    dataArray: dataArrayRef,
    startRecording,
    stopRecording,
    reset,
  }
}
